import moment from "moment";
import {isEmpty} from "lodash";
import {DEFAULT_DELIVERY_MIN_NOTICE, DELIVERY_PROVIDER_DEADLINE_HOUR} from "../MarketplaceUtils.jsx";

export const calculateTimeRanges = (
    isShortRange,
    providerAvailability,
    selectedDate,
    deliveryArrivingTimeRange,
    businessStartHourForAdmin,
    businessStartMinuteForAdmin
) => {
    if (!isShortRange || !providerAvailability || !selectedDate) return [];

    const ranges = [];
    const dayOfWeek = moment(selectedDate).format("dddd").toLowerCase();
    const dayAvailability = providerAvailability[dayOfWeek];

    if (!dayAvailability?.selected) return [];

    const [startHour, startMinute] = dayAvailability.start.split(":").map(Number);
    const [endHour, endMinute] = dayAvailability.end.split(":").map(Number);

    // Use the selected date instead of today's date for accurate time calculations
    const selectedDateMoment = moment(selectedDate);
    const businessStartHour = businessStartHourForAdmin ? businessStartHourForAdmin : startHour;
    const businessStartMinute = businessStartMinuteForAdmin ? businessStartMinuteForAdmin : startMinute;
    const businessStart = selectedDateMoment
        .clone()
        .set({hour: businessStartHour, minute: businessStartMinute, second: 0, millisecond: 0});
    const businessEnd = selectedDateMoment.clone().set({hour: endHour, minute: endMinute, second: 0, millisecond: 0});

    let currentTime = businessStart.clone();
    while (currentTime.clone().add(deliveryArrivingTimeRange, "hours").isSameOrBefore(businessEnd)) {
        const rangeStart = currentTime.clone();
        const rangeEnd = currentTime.clone().add(deliveryArrivingTimeRange, "hours");

        const displayLabel = `${rangeStart.format("HH:mm")} - ${rangeEnd.format("HH:mm")}`;
        const value = displayLabel;

        ranges.push({
            label: displayLabel,
            value: value,
            startTime: rangeStart.clone(),
            endTime: rangeEnd.clone()
        });

        currentTime.add(30, "minutes");
    }

    return ranges;
};

export const formatSelectedTimeRange = (isShortRange, startTime, endTime) => {
    if (!isShortRange || !startTime || !endTime) return null;

    const startStr = moment(startTime).format("HH:mm");
    const endStr = moment(endTime).format("HH:mm");
    return `${endStr} - ${startStr}`;
};

export const findMatchingTimeRange = (isShortRange, startTime, endTime, timeRanges) => {
    if (!isShortRange || !startTime || !endTime || !timeRanges?.length) {
        return null;
    }

    const startTimeStr = moment(startTime).format("HH:mm");
    const endTimeStr = moment(endTime).format("HH:mm");

    const matchingRange = timeRanges.find(range => {
        const rangeStartStr = moment(range.startTime).format("HH:mm");
        const rangeEndStr = moment(range.endTime).format("HH:mm");
        return rangeStartStr === startTimeStr && rangeEndStr === endTimeStr;
    });

    return matchingRange?.value || null;
};

export const formatMinNoticeText = minNotice => {
    const minNoticeDays = Math.floor((minNotice ?? DEFAULT_DELIVERY_MIN_NOTICE) / 24);
    const minNoticeHours = (minNotice ?? DEFAULT_DELIVERY_MIN_NOTICE) % 24;
    return `
        ${minNoticeDays > 0 ? ` ${minNoticeDays} ימים` : ""}
        ${minNoticeDays > 0 && minNoticeHours > 0 ? "ו" : " "}
        ${minNoticeHours > 0 ? `${minNoticeHours} שעות` : ""}`;
};

export const isDayAvailable = (selectedTime, providerAvailability) => {
    if (isEmpty(providerAvailability)) {
        return true;
    }

    const dayFormat = moment(selectedTime).format("dddd");
    const dayAvailability = providerAvailability[dayFormat.toLowerCase()];
    return dayAvailability?.selected === true;
};

export const isTimeInProviderBusinessHours = (selectedTime, deliveryMaxTime, providerAvailability) => {
    if (isEmpty(providerAvailability)) {
        return true;
    }

    return [selectedTime, deliveryMaxTime].every(testedTime => {
        const timeFormat = moment(testedTime).format("HH:mm");
        const dayFormat = moment(testedTime).format("dddd");
        const {start, end, selected} = providerAvailability[dayFormat.toLowerCase()];
        return (
            selected &&
            moment(timeFormat, "HH:mm").isSameOrAfter(moment(start, "HH:mm")) &&
            moment(timeFormat, "HH:mm").isSameOrBefore(moment(end, "HH:mm"))
        );
    });
};

export const isTimeInNextDayAvailability = (selectedTime, nextDayAvailability) => {
    if (!nextDayAvailability) {
        return true;
    }
    const isSelectedForTomorrow = moment(selectedTime).isSame(moment().add(1, "day"), "day");
    if (!isSelectedForTomorrow) {
        return true;
    }

    const cutoff = moment(nextDayAvailability);
    const cutoffOnSelectedDay = moment().set({
        hour: cutoff.hour(),
        minute: cutoff.minute(),
        second: 0,
        millisecond: 0
    });

    return !moment().isSameOrAfter(cutoffOnSelectedDay);
};

export const isTimeInShippingDeliveryBusinessHours = selectedTime => {
    const isSelectedTimeIsToday = moment().isSame(selectedTime, "day");
    if (isSelectedTimeIsToday) {
        return false;
    }

    const isSelectedTimeIsTomorrow = selectedTime.isSame(moment().add(1, "day"), "day");
    const isCurrentTimeAfterDeliveryProviderDeadline = moment().isAfter(
        moment().set({
            hour: DELIVERY_PROVIDER_DEADLINE_HOUR,
            minute: 0,
            second: 0,
            millisecond: 0
        })
    );
    if (isCurrentTimeAfterDeliveryProviderDeadline && isSelectedTimeIsTomorrow) {
        return false;
    }
    return true;
};
